import { cleanText } from "./ident-utils.js";

function normaliseTimestampCandidate(value) {
  if (value === undefined || value === null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.toISOString() : null;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const date = new Date(trimmed);
    if (Number.isFinite(date.getTime())) {
      return date.toISOString();
    }
    return trimmed;
  }
  if (typeof value === "object" && value !== null && "toISOString" in value) {
    try {
      const iso = value.toISOString();
      const parsed = new Date(iso);
      return Number.isFinite(parsed.getTime()) ? iso : null;
    } catch {
      return null;
    }
  }
  return null;
}

function pickTimestamp(candidateList = []) {
  for (const candidate of candidateList) {
    const resolved = normaliseTimestampCandidate(candidate);
    if (resolved) {
      return resolved;
    }
  }
  return null;
}

function normaliseStatusCode(value) {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const match = trimmed.match(/\b(\d{3})\b/);
    if (match) {
      const parsed = Number.parseInt(match[1], 10);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function extractIdent(params) {
  if (!params || typeof params !== "object") {
    return null;
  }
  const candidates = [
    params.ident,
    params.registration,
    params.flight,
    params.flight_id,
    params.fa_flight_id,
    params.callsign,
  ];
  for (const value of candidates) {
    const cleaned = cleanText(value);
    if (cleaned) {
      return cleaned;
    }
  }
  return null;
}

function extractError(latest) {
  if (!latest || typeof latest !== "object") {
    return null;
  }
  const candidates = [
    latest.error,
    latest.response_error,
    latest.error_message,
    latest.message,
  ];
  const payload = latest.payload;
  if (payload && typeof payload === "object") {
    candidates.push(payload.error, payload.error_message, payload.message, payload.detail);
  }
  for (const value of candidates) {
    const cleaned = cleanText(value);
    if (cleaned) {
      return cleaned;
    }
  }
  return null;
}

function extractEndpoint(latest) {
  if (!latest || typeof latest !== "object") {
    return null;
  }
  return cleanText(latest.endpoint) || null;
}

export function summariseLastFlightawareQuery(result) {
  const data = result && typeof result === "object" ? result : {};
  const latest = data.latest && typeof data.latest === "object" ? data.latest : null;
  const timestamp = pickTimestamp([
    latest?.queried_at,
    latest?.requested_at,
    latest?.timestamp,
    data.last_query_at,
  ]);
  const endpoint = extractEndpoint(latest);
  const ident = extractIdent(latest?.params ?? latest);
  const statusCode = normaliseStatusCode(latest?.status ?? latest?.response_status);
  const statusText = cleanText(latest?.status) || (statusCode ? String(statusCode) : null);
  const error = extractError(latest);
  const failure = Boolean(error) || (Number.isFinite(statusCode) ? statusCode >= 400 : false);
  const rowCount = Number.isFinite(data.rowCount) ? Number(data.rowCount) : null;
  const skipped = Boolean(data.skipped);

  return {
    timestamp,
    endpoint,
    ident,
    statusCode,
    statusText,
    error,
    failure,
    rowCount,
    skipped,
  };
}
